/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { AppRoutes } from '@/constants/routes.ts';
import { Utils } from '@/utils/utils.ts';
import { NavLink } from 'react-router';
import { MonitorProcessData } from '@/types/models';
import { useJobsProcessInfoData } from './JobsMonitorContext';
import { Skeleton } from 'primereact/skeleton';
import { useProjectPath } from '@/hooks/useProjectPath';

interface Props {
  className?: string;
  monitorData: MonitorProcessData;
}

export const ProcessInfo: React.FC<Props> = ({ className, monitorData }) => {
  const { processesInfoData } = useJobsProcessInfoData();

  const { toBrowserProjectPath } = useProjectPath();

  const rowProcessInfo = useMemo(() => {
    if (processesInfoData && processesInfoData) {
      const rowInfo = processesInfoData?.find(
        (el) => el.fecthStatus === 'sucessfull' && el.processId == monitorData.id
      );
      return rowInfo;
    }
  }, [processesInfoData, monitorData]);

  const isFetchInfo = useMemo(() => {
    if (!rowProcessInfo) {
      const emptyRowData = processesInfoData?.some(
        (el) => el.processId == monitorData.id && el.fecthStatus === 'failed'
      );
      return !emptyRowData && !monitorData.id.startsWith('dyna~') && true;
    }
    return rowProcessInfo.fecthStatus === 'pending';
  }, [rowProcessInfo, monitorData, processesInfoData]);

  const url = () => {
    if (!rowProcessInfo?.data) {
      return '#';
    }
    return (
      AppRoutes.projects.detail.url(toBrowserProjectPath(rowProcessInfo.data.link)) +
      Utils.buildWithQueryParamsUrl({
        process_global: rowProcessInfo.data.process_id,
        tabs: '1'
      })
    );
  };
  const LENGTH = 15;
  const stripPrefix = Utils.hideProcessPrefix(monitorData.id ?? '');
  const truncate_text = stripPrefix.length > LENGTH ? stripPrefix.slice(0, LENGTH).concat('...') : stripPrefix;

  return (
    <>
      {isFetchInfo ? (
        <Skeleton width={'100%'} height="1rem" />
      ) : (
        <div className={classNames(className, 'flex align-items-center w-fit cursor-pointer')}>
          <i
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(monitorData.id ?? monitorData.id).then(() => {
                const target = e.target as HTMLElement;
                target.classList.add('text-green-700', 'pi-check-square');
                setTimeout(() => {
                  target.classList.remove('text-green-700', 'pi-check-square');
                }, 1000);
              });
            }}
            className="pi pi-clipboard mr-1"
          />

          <NavLink className={classNames(className, 'text-color font-medium hover:text-primary')} to={url()}>
            <span className="underline hover:text-primary">{truncate_text}</span>
          </NavLink>
        </div>
      )}
    </>
  );
};
